import React from 'react'
import {
  $getRoot,
  $insertNodes,
  EditorState,
  LexicalEditor,
  ParagraphNode,
} from 'lexical'
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListNode, ListItemNode } from '@lexical/list'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { HeadingNode } from '@lexical/rich-text'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { useDebouncedCallback } from 'use-debounce'

import { editorTheme } from './editorTheme'
import { ToolbarPlugin } from './EditorPlugins/ToolbarPlugin'
import { EditLinkPlugin } from './EditorPlugins/EditLinkPlugin'
import { notNullOrUndefinedOrEmpty } from './utils'

interface EditorProps {
  html?: string
  onChange: (html: string) => void
  onError?: (error: Error) => void
  config?: {
    namespace?: string
    editorContentMinHeight?: string
    editorMaxHeight?: string
  }
}

const initalConfig = {
  namespace: 'editor',
  editorContentMinHeight: '12rem',
  editorMaxHeight: '30rem',
} as const

export const Editor: React.FC<EditorProps> = ({
  html,
  onChange,
  onError,
  config: {
    namespace = initalConfig.namespace,
    editorContentMinHeight = initalConfig.editorContentMinHeight,
    editorMaxHeight = initalConfig.editorMaxHeight,
  } = initalConfig,
}) => {
  // Stable reference, so OnChangePlugin does not re-register its listener on every render
  const handleOnChange = useDebouncedCallback(
    (editorState: EditorState, editor: LexicalEditor) =>
      editorState.read(() => {
        const root = $getRoot()
        const textContent = root.getTextContent()
        if (!textContent) {
          onChange('')
        } else {
          onChange($generateHtmlFromNodes(editor))
        }
      }),
    0
  )

  const handleOnError = (error: Error) => {
    if (onError) onError(error)
    // eslint-disable-next-line no-console
    console.error(error)
  }

  const initialConfig: InitialConfigType = {
    namespace,
    theme: editorTheme,
    onError: handleOnError,
    editorState: (editor) => {
      if (!html) return

      editor.update(() => {
        const parser = new DOMParser()
        const dom = parser.parseFromString(html, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)

        $insertNodes(nodes)
      })
    },
    nodes: [
      ListNode,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      HeadingNode,
      ParagraphNode,
    ].filter(notNullOrUndefinedOrEmpty),
  }

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className="border border-common-gray-5 rounded-md">
        <ToolbarPlugin />

        <div style={{ maxHeight: editorMaxHeight }} className="overflow-y-auto">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                style={{ minHeight: editorContentMinHeight }}
                className="p-3 outline-none"
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />

          <ListPlugin />
          <LinkPlugin />
          <EditLinkPlugin />
          <HistoryPlugin />
          <OnChangePlugin onChange={handleOnChange} />
        </div>
      </div>
    </LexicalComposer>
  )
}
